import {createSlice, type SliceSelectors} from '@reduxjs/toolkit'
import {LOCATION_CHANGE} from 'redux-first-history'

import {formatISO, makeISODateString} from '@shared/helpers/date'
import {qs} from '@shared/helpers/queryString'

import {getDeliveryTag} from '@web/helpers/policies'
import {
    initDeliveryTracking,
    DeliveryStatus,
    Operator,
    DeliveryTrackingState,
    getDeliveryMethod,
    getDeliveryStatus,
    getDepartureDateTime,
    getEstimatedArrivalTime,
    getInitialState,
} from '@web/slices/deliveryTracking/slice'
import {LocationChangeAction} from '@web/slices/router'

import {DeliveryTrackingReducers, MakeCreateSliceOptions} from './interfaces'

export default (options?: MakeCreateSliceOptions) =>
    createSlice<
        DeliveryTrackingState,
        DeliveryTrackingReducers,
        'deliveryTracking',
        SliceSelectors<DeliveryTrackingState>
    >({
        name: 'deliveryTracking',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase<typeof LOCATION_CHANGE, LocationChangeAction>(LOCATION_CHANGE, (state, {payload}) => {
                    const {location} = payload
                    const {user} = qs.parse(location?.search ?? '')

                    state.operatorType = (() => {
                        if (user === 'seller') {
                            return Operator.SELLER
                        } else {
                            return Operator.CUSTOMER
                        }
                    })()
                })
                .addCase(initDeliveryTracking.fulfilled, (state, {payload}) => {
                    const {deliveryTracking, orderDelivery, deliveryReviewWritable} = payload

                    if (deliveryTracking && orderDelivery) {
                        state.arrivalInfo = {
                            departureDateTime: getDepartureDateTime({
                                deliveryDateTime: deliveryTracking.deliveryDateTime,
                                branchDepartureDate:
                                    orderDelivery.deliveryInfo.branchDelivery?.deliveryDate ?? undefined,
                                branchDepartureTime:
                                    orderDelivery.deliveryInfo.branchDelivery?.deliveryStartTime ?? undefined,
                                deliveryAttributeType: orderDelivery.productOrder.deliveryAttributeType ?? undefined,
                                salesChannelType: orderDelivery.productOrder.saleChannelType,
                            }),
                            departureBufferedDateTime: makeISODateString({
                                date: orderDelivery.deliveryInfo.branchDelivery?.deliveryDate ?? undefined,
                                time: orderDelivery.deliveryInfo.branchDelivery?.deliveryEndTime ?? undefined,
                            }),
                            estimatedDateTime: getEstimatedArrivalTime({
                                arrivalGuaranteeDateTime:
                                    orderDelivery.deliveryInfo.arrivalGuarantee?.arrivalGuaranteeDateTime ?? undefined,
                                predictionDate: orderDelivery.deliveryInfo.deliveryPrediction?.predictionDate,
                                deliveryAttributeType: orderDelivery.productOrder.deliveryAttributeType ?? undefined,
                            }),
                            completedDateTime: deliveryTracking.deliveryCompleteDateTime ?? undefined,
                            probability: orderDelivery.deliveryInfo.deliveryPrediction?.prediction,
                            minutesETA: orderDelivery.deliveryInfo.branchDelivery?.deliveryDurationMinutes ?? undefined,
                        }
                        state.deliveryMethod = getDeliveryMethod({
                            deliveryAttributeType: orderDelivery?.productOrder.deliveryAttributeType ?? undefined,
                            arrivalGuaranteeDateTime:
                                orderDelivery?.deliveryInfo.arrivalGuarantee?.arrivalGuaranteeDateTime ?? undefined,
                            deliverySimulatorProbability: orderDelivery?.deliveryInfo.deliveryPrediction?.prediction,
                            deliverySimulatorPredictionDate:
                                orderDelivery?.deliveryInfo.deliveryPrediction?.predictionDate,
                            saleChannelType: orderDelivery?.productOrder.saleChannelType,
                            deliveryMethod: deliveryTracking.deliveryMethodType,
                            deliveryType: deliveryTracking.deliveryType,
                            trackable: deliveryTracking.trackable,
                        })
                        state.deliveryStatus = getDeliveryStatus({
                            status: deliveryTracking.deliveryStatusType ?? undefined,
                            direction: deliveryTracking.deliveryType,
                            expired: deliveryTracking.expired,
                            trackable: deliveryTracking.trackable,
                            ableToPurchaseConfirm: orderDelivery.productOrder.ableToPurchaseDecision,
                        })
                    }

                    if (deliveryTracking) {
                        state.deliveryId = deliveryTracking.deliveryId
                        state.deliveryDirection = deliveryTracking.deliveryType
                        state.tracking = {
                            events:
                                deliveryTracking.stackTrace?.details.map((event) => ({
                                    title: event.branchName ?? undefined,
                                    processDateTime: event.processDateTime,
                                    eventType: event.deliveryStatusType ?? undefined,
                                    eventDescription: event.deliveryStatusName ?? undefined,
                                })) || [],
                            interlockingCorpType: deliveryTracking.interlockingCorpType || undefined,
                        }
                        state.courier = deliveryTracking.stackTrace
                            ? {
                                  name: deliveryTracking.stackTrace.deliveryCompanyName,
                                  companyTel: deliveryTracking.stackTrace.deliveryCompanyTel ?? undefined,
                                  driverTel: deliveryTracking.stackTrace.lastDetail?.driverTel ?? undefined,
                                  invoiceNumber: deliveryTracking.stackTrace.invoiceNo,
                              }
                            : undefined
                    } else {
                        state.deliveryStatus = DeliveryStatus.FAILED
                    }

                    if (orderDelivery) {
                        const orderDateTime = formatISO(orderDelivery.orderDateTime)

                        state.deliveryAttributeType = orderDelivery.productOrder.deliveryAttributeType ?? undefined
                        state.deliveryTagType =
                            getDeliveryTag({
                                orderDate: orderDateTime,
                                arrivalDate:
                                    orderDelivery.deliveryInfo.arrivalGuarantee?.arrivalGuaranteeDateTime ?? undefined,
                                deliveryAttributeType: orderDelivery.productOrder.deliveryAttributeType ?? undefined,
                                deliveryTagType: orderDelivery.productOrder.deliveryTagType ?? undefined,
                            }) ?? undefined

                        state.orderSummary = {
                            orderNo: orderDelivery.productOrder.orderNo,
                            orderDateTime,
                            orderAmount: orderDelivery.productOrder.orderAmount,
                            mainPaymentMethod: orderDelivery.generalPayMeansClassType,
                            isGiftReceiver: orderDelivery.giftingReceiver,
                            product: {
                                orderNo: orderDelivery.productOrder.productOrderNo,
                                name: orderDelivery.productOrder.productName,
                                productUrl: orderDelivery.productOrder.productUrl ?? undefined,
                                imageUrl: orderDelivery.productOrder.productImageUrl ?? undefined,
                                ecNvMid: orderDelivery.productOrder.ecNvMid ?? undefined,
                                bundleProductCount: orderDelivery.deliveryInfo.productOrderCount ?? undefined,
                                category: {
                                    large: orderDelivery.productOrder.largeCategoryId ?? undefined,
                                    middle: orderDelivery.productOrder.middleCategoryId ?? undefined,
                                    small: orderDelivery.productOrder.smallCategoryId ?? undefined,
                                    detail: orderDelivery.productOrder.detailCategoryId ?? undefined,
                                },
                            },
                            merchant: {
                                name: orderDelivery.merchant.merchantName,
                                branchName: orderDelivery.deliveryInfo.branchDelivery?.branchName ?? undefined,
                                imageUrl: orderDelivery.merchant.merchantImageUrl ?? undefined,
                                no: orderDelivery.merchant.merchantNo,
                                phone: orderDelivery.merchant.merchantTelNo ?? undefined,
                                email: orderDelivery.merchant.merchantEmailAddress ?? undefined,
                                status: orderDelivery.merchant.merchantStateType ?? undefined,
                                isTalkEnabled: orderDelivery.merchant.useBiztalk,
                                isLogisticsDirectContracted: orderDelivery.merchant.logisticsDirectContracted,
                            },
                            rewardPoint: orderDelivery.productOrder.ableToPurchaseDecision
                                ? (orderDelivery.productOrder.purchaseDecisionRewardAmount ?? 0)
                                : undefined,
                        }
                        state.writableMap = deliveryReviewWritable?.writableMap ?? {}
                    }
                })
            options?.extraReducers?.(builder)
        },
    })
